import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import { io, Socket } from "socket.io-client";
import { SOCKET_IO_TRANSPORTS } from "@constants/socketIoTransports";
import type { SafetyNotificationPayload, SafetyNotificationItem } from "interface/notification";

const getSocketUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";
  const base = apiUrl.replace(/\/api\/?$/, "").replace(/\/+$/, "");
  const protocol = base.startsWith("https") ? "wss" : "ws";
  const host = base.replace(/^https?:\/\//, "");
  return `${protocol}://${host}`;
};

const STORAGE_KEY_READ_IDS = "tovride_admin_safety_read_ids";
const STORAGE_KEY_LIST = "tovride_admin_safety_list";
const MAX_SAVED_LIST = 100;

function getReadIds(): Set<string> {
  try {
    if (typeof window === "undefined") return new Set();
    const raw = localStorage.getItem(STORAGE_KEY_READ_IDS);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? new Set(arr as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function setReadIds(ids: Set<string>) {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY_READ_IDS, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

function getSafetyListFromStorage(): SafetyNotificationItem[] {
  try {
    if (typeof window === "undefined") return [];
    const raw = localStorage.getItem(STORAGE_KEY_LIST);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr as SafetyNotificationItem[];
  } catch {
    return [];
  }
}

function setSafetyListToStorage(list: SafetyNotificationItem[]) {
  try {
    if (typeof window === "undefined") return;
    const toSave = list.slice(0, MAX_SAVED_LIST);
    localStorage.setItem(STORAGE_KEY_LIST, JSON.stringify(toSave));
  } catch {
    // ignore
  }
}

export interface UseAdminNotificationsSocketOptions {
  /** Called when any admin notification event is received (safety, booking, kyc) - use to refetch admin notifications API. */
  onNotification?: () => void;
}

export function useAdminNotificationsSocket(
  token: string | null,
  options: UseAdminNotificationsSocketOptions = {}
) {
  const { onNotification } = options;
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const [safetyList, setSafetyList] = useState<SafetyNotificationItem[]>(
    getSafetyListFromStorage
  );
  const [readIds, setReadIdsState] = useState<Set<string>>(getReadIds);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const markSafetyAsRead = useCallback((safetyId: string) => {
    setReadIdsState((prev) => {
      const next = new Set(prev);
      next.add(safetyId);
      setReadIds(next);
      return next;
    });
  }, []);

  const markAllSafetyAsRead = useCallback(() => {
    setSafetyList((list) => {
      const ids = new Set(list.map((n) => n.safetyId));
      setReadIds(ids);
      setReadIdsState(ids);
      return list.map((n) => ({ ...n, read: true }));
    });
  }, []);

  useEffect(() => {
    if (!token?.trim()) {
      setSafetyList([]);
      setConnected(false);
      return;
    }
    setSafetyList(getSafetyListFromStorage());

    const baseUrl = getSocketUrl();
    const socket = io(`${baseUrl}/admin-notifications`, {
      auth: { token },
      transports: [...SOCKET_IO_TRANSPORTS],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    socket.on("safety:notification", (payload: SafetyNotificationPayload) => {
      const persistedRead = getReadIds();
      setSafetyList((prev) => {
        const item: SafetyNotificationItem = {
          ...payload,
          read: persistedRead.has(payload.safetyId),
        };
        const filtered = prev.filter((n) => n.safetyId !== payload.safetyId);
        const next = [item, ...filtered];
        setSafetyListToStorage(next);
        return next;
      });
      onNotificationRef.current?.();
    });

    socket.on("booking:created", () => {
      onNotificationRef.current?.();
    });

    socket.on("kyc:request", () => {
      onNotificationRef.current?.();
    });

    socket.on("error", (err: { message?: string }) => {
      console.warn("Admin notifications socket error:", err?.message);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [token]);

  const unreadSafetyCount = safetyList.filter((n) => !readIds.has(n.safetyId)).length;

  const safetyNotifications = useMemo(
    () =>
      safetyList.map((n) => ({
        ...n,
        read: readIds.has(n.safetyId),
      })),
    [safetyList, readIds]
  );

  return {
    safetyNotifications,
    markSafetyAsRead,
    markAllSafetyAsRead,
    unreadSafetyCount,
    connected,
  };
}
